/** Мелочи раздела «Смена»: время по часам ресторана, снимки, пометки статусов. */

import { useEffect, useState } from 'react';
import { getRestaurantTimeZone } from '@shell/format';
import { photoObjectUrl } from '../../api';
import { Pill } from '../../ui';
import type { PhotoRef } from './api';

/** «08:30» по часам ресторана. */
export function hhmm(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: getRestaurantTimeZone(),
  }).format(date);
}

/** «16.09 08:30» по часам ресторана. */
export function dayTime(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: getRestaurantTimeZone(),
  }).format(date);
}

/** Сдвиг даты ISO на дни: «2026-09-16» + (-1) → «2026-09-15». */
export function shiftDay(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d + days));
  return date.toISOString().slice(0, 10);
}

const RUN_COLOURS: Record<string, string | null> = {
  done: 'green',
  done_late: 'yellow',
  missed: 'red',
  in_progress: 'yellow',
  pending: null,
};

export function RunStatus({ status, title }: { status: string; title: string }) {
  return <Pill colour={RUN_COLOURS[status] ?? null}>{title}</Pill>;
}

/** Снимок пункта или задачи. Внутри Telegram личность едет заголовком — `photoObjectUrl`. */
export function Photo({ photo, alt }: { photo: PhotoRef; alt: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!photo.url) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    photoObjectUrl(photo.url)
      .then((value) => {
        if (value.startsWith('blob:')) objectUrl = value;
        if (!cancelled) setSrc(value);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [photo.url]);

  if (!photo.available || !photo.url) {
    return <span className="shift-photo shift-photo--gone">снимок удалён по сроку</span>;
  }
  if (failed) return <span className="shift-photo shift-photo--gone">фото не загрузилось</span>;
  return (
    <a
      className="shift-photo"
      href={src ?? undefined}
      target="_blank"
      rel="noreferrer"
      aria-label={`${alt} — открыть фото`}
    >
      {src ? <img src={src} alt={alt} loading="lazy" /> : <span className="faint">…</span>}
      {photo.forwarded && <span className="shift-photo__mark">переслано</span>}
    </a>
  );
}

export function Photos({ photos, alt }: { photos: PhotoRef[]; alt: string }) {
  if (photos.length === 0) return null;
  return (
    <div className="shift-photos">
      {photos.map((photo) => (
        <Photo key={photo.photo_id} photo={photo} alt={alt} />
      ))}
    </div>
  );
}

/** Слово после числа: 1 чек-лист, 2 чек-листа, 5 чек-листов. */
export function plural(count: number, one: string, few: string, many: string): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) return few;
  return many;
}
